import { api } from '../api/client';
import { translate } from '../i18n/i18n';

export type WhoAmI = {
  userId: string;
  accountId: string;
  /** False when this person is working inside somebody else's account. */
  ownAccount: boolean;
  role: 'OWNER' | 'MEMBER';
};

export type Member = {
  memberId: string;
  email: string | null;
  displayName: string | null;
  role: 'OWNER' | 'MEMBER';
  /** Which row is the person reading it. */
  you: boolean;
};

export type Pending = {
  invitationId: string;
  email: string;
  expiresAtEpochMs: number;
};

export type InviteResult = {
  invitationId: string;
  email: string;
  /** False when no mail could be sent, in which case the link has to be passed on by hand. */
  emailed: boolean;
  link: string;
  expiresAtEpochMs: number;
};

export async function whoAmI(): Promise<WhoAmI> {
  return api<WhoAmI>('/me/account', { token: true });
}

export async function fetchMembers(): Promise<Member[]> {
  return api<Member[]>('/me/account/members', { token: true });
}

export async function fetchPending(): Promise<Pending[]> {
  return api<Pending[]>('/me/account/invitations', { token: true });
}

export async function invite(email: string): Promise<InviteResult> {
  return api<InviteResult>('/me/account/invitations', {
    method: 'POST', token: true, body: { email },
  });
}

export async function withdraw(invitationId: string): Promise<void> {
  await api<void>(`/me/account/invitations/${invitationId}`, { method: 'DELETE', token: true });
}

export async function acceptInvitation(token: string): Promise<Member> {
  return api<Member>('/me/account/invitations/accept', {
    method: 'POST', token: true, body: { token },
  });
}

export async function leaveAccount(): Promise<void> {
  await api<void>('/me/account/leave', { method: 'POST', token: true });
}

export async function removeMember(memberId: string): Promise<void> {
  await api<void>(`/me/account/members/${memberId}`, { method: 'DELETE', token: true });
}

export function describeExpiry(epochMs: number): string {
  const hours = Math.max(0, Math.round((epochMs - Date.now()) / 3_600_000));
  if (hours <= 0) return translate('expired');
  if (hours === 1) return translate('good for another hour');
  return translate('good for another {hours} hours', { hours });
}
